<template>
  <div class="particle-animation" :class="{ 'full-height': fullHeight }">
    <canvas ref="canvas" class="particle-canvas"></canvas>
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'ParticleAnim',
  props: {
    fullHeight: {
      type: Boolean,
      default: false
    },
    particleCount: {
      type: Number,
      default: 50
    },
    particleColor: {
      type: String,
      default: 'rgba(67, 97, 238, 0.6)' // $primary with opacity
    },
    connectionColor: {
      type: String,
      default: 'rgba(67, 97, 238, 0.2)'
    },
    particleSpeed: {
      type: Number,
      default: 0.5
    },
    connectionDistance: {
      type: Number,
      default: 100
    },
    particleSize: {
      type: Number,
      default: 2
    }
  },
  computed: {
    currentParticleColor() {
      return this.$vuetify.theme.dark
        ? 'rgba(67, 97, 238, 0.6)'
        : 'rgba(67, 97, 238, 0.3)'
    },
    currentConnectionColor() {
      return this.$vuetify.theme.dark
        ? 'rgba(67, 97, 238, 0.2)'
        : 'rgba(67, 97, 238, 0.1)'
    }
  },
  watch: {
    '$vuetify.theme.dark'() {
      // Re-render particles when theme changes
      if (this.ctx) {
        this.animate()
      }
    }
  },
  data() {
    return {
      canvas: null,
      ctx: null,
      particles: [],
      animationId: null,
      mouse: {
        x: null,
        y: null,
        radius: 150
      }
    }
  },
  mounted() {
    this.initParticles()
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('mouseout', this.handleMouseOut)
  },
  beforeDestroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mouseout', this.handleMouseOut)
  },
  methods: {
    initParticles() {
      this.canvas = this.$refs.canvas
      this.ctx = this.canvas.getContext('2d')

      this.handleResize()
      this.createParticles()
      this.animate()
    },

    createParticles() {
      this.particles = []
      for (let i = 0; i < this.particleCount; i++) {
        this.particles.push({
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          dx: (Math.random() - 0.5) * this.particleSpeed,
          dy: (Math.random() - 0.5) * this.particleSpeed,
          size: Math.random() * this.particleSize + 1,
          opacity: Math.random() * 0.5 + 0.5
        })
      }
    },

    handleResize() {
      if (!this.canvas) return

      this.canvas.width = this.canvas.offsetWidth
      this.canvas.height = this.canvas.offsetHeight

      if (this.particles.length > 0) {
        // Reposition particles if canvas size changed
        this.particles.forEach(particle => {
          particle.x = Math.min(particle.x, this.canvas.width)
          particle.y = Math.min(particle.y, this.canvas.height)
        })
      }
    },

    handleMouseMove(event) {
      const rect = this.canvas.getBoundingClientRect()
      this.mouse.x = event.clientX - rect.left
      this.mouse.y = event.clientY - rect.top
    },

    handleMouseOut() {
      this.mouse.x = null
      this.mouse.y = null
    },

    animate() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

      // Update and draw particles
      this.particles.forEach(particle => {
        this.updateParticle(particle)
        this.drawParticle(particle)
      })

      // Draw connections
      this.drawConnections()

      // Mouse interaction
      if (this.mouse.x && this.mouse.y) {
        this.drawMouseConnections()
      }

      this.animationId = requestAnimationFrame(this.animate)
    },

    updateParticle(particle) {
      particle.x += particle.dx
      particle.y += particle.dy

      // Bounce off edges
      if (particle.x + particle.size > this.canvas.width || particle.x - particle.size < 0) {
        particle.dx = -particle.dx
      }
      if (particle.y + particle.size > this.canvas.height || particle.y - particle.size < 0) {
        particle.dy = -particle.dy
      }

      // Mouse attraction effect
      if (this.mouse.x && this.mouse.y) {
        const dx = this.mouse.x - particle.x
        const dy = this.mouse.y - particle.y
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (distance < this.mouse.radius) {
          const forceDirectionX = dx / distance
          const forceDirectionY = dy / distance
          const maxDistance = this.mouse.radius
          const force = (maxDistance - distance) / maxDistance
          const directionX = forceDirectionX * force * 0.6
          const directionY = forceDirectionY * force * 0.6

          particle.x += directionX
          particle.y += directionY
        }
      }
    },

    drawParticle(particle) {
      this.ctx.beginPath()
      this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2, false)

      // Create gradient for particle
      const gradient = this.ctx.createRadialGradient(
        particle.x, particle.y, 0,
        particle.x, particle.y, particle.size
      )
      gradient.addColorStop(0, this.currentParticleColor)
      gradient.addColorStop(1, 'transparent')

      this.ctx.fillStyle = gradient
      this.ctx.fill()
    },

    drawConnections() {
      for (let i = 0; i < this.particles.length; i++) {
        for (let j = i + 1; j < this.particles.length; j++) {
          const distance = this.getDistance(this.particles[i], this.particles[j])

          if (distance < this.connectionDistance) {
            const opacity = 1 - distance / this.connectionDistance
            this.ctx.strokeStyle = this.currentConnectionColor.replace(/[\d.]+\)$/, `${opacity * (this.$vuetify.theme.dark ? 0.2 : 0.1)})`)
            this.ctx.lineWidth = 0.5
            this.ctx.beginPath()
            this.ctx.moveTo(this.particles[i].x, this.particles[i].y)
            this.ctx.lineTo(this.particles[j].x, this.particles[j].y)
            this.ctx.stroke()
          }
        }
      }
    },

    drawMouseConnections() {
      this.particles.forEach(particle => {
        const distance = this.getDistance(this.mouse, particle)

        if (distance < this.mouse.radius) {
          const opacity = 1 - distance / this.mouse.radius
          this.ctx.strokeStyle = this.currentConnectionColor.replace(/[\d.]+\)$/, `${opacity * (this.$vuetify.theme.dark ? 0.4 : 0.2)})`)
          this.ctx.lineWidth = 1
          this.ctx.beginPath()
          this.ctx.moveTo(particle.x, particle.y)
          this.ctx.lineTo(this.mouse.x, this.mouse.y)
          this.ctx.stroke()
        }
      })
    },

    getDistance(point1, point2) {
      const dx = point1.x - point2.x
      const dy = point1.y - point2.y
      return Math.sqrt(dx * dx + dy * dy)
    }
  }
}
</script>

<style lang="scss" scoped>
.particle-animation {
  position: relative;
  width: 100%;
  overflow: hidden;

  &.full-height {
    height: 100vh;
    min-height: 100vh;
  }

  &:not(.full-height) {
    height: 100%;
    min-height: 400px;
  }
}

.particle-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.particle-animation > * {
  position: relative;
  z-index: 2;
}
</style>